import React from "react";
import { useOutletContext, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { 
  FaWallet, FaCoins, FaHistory, FaClock, 
  FaInfoCircle, FaGift, FaExchangeAlt, FaDownload,
  FaCheckCircle, FaPlusCircle, FaMinusCircle, FaFilter,
  FaTimes, FaChartLine, FaTrophy, FaUserFriends
} from "react-icons/fa";
import { API_BASE_URL } from "../utils/config";
import axios from "axios";
import * as XLSX from "xlsx";
import { toast } from "react-toastify";

const CandidateWallet = () => {
  const { profile } = useOutletContext();
  const navigate = useNavigate();

  const [walletData, setWalletData] = React.useState(null);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState(null);
  const [showHistory, setShowHistory] = React.useState(false);
  const [filterType, setFilterType] = React.useState("all");
  const [showFilterDropdown, setShowFilterDropdown] = React.useState(false);
  const [showExportMenu, setShowExportMenu] = React.useState(false);

  React.useEffect(() => {
    if (profile?._id) {
      fetchWalletData();
    }
  }, [profile?._id]);

  const fetchWalletData = async () => {
    try {
      setLoading(true);
      const candidateId = profile?._id || profile?.candidateId;
      if (!candidateId) {
        throw new Error("Candidate ID not found");
      }
      
      const response = await axios.get(`${API_BASE_URL}/candidate/mywallet/${candidateId}`);
      
      if (response.data?.success) {
        setWalletData(response.data.data);
        setError(null);
      } else {
        setError("Failed to fetch wallet data");
      }
    } catch (err) {
      console.error("Error fetching wallet:", err);
      setError(err.response?.data?.message || "Unable to load wallet. Please try again later.");
    } finally {
      setLoading(false);
    }
  };

  const getFilteredHistory = () => {
    if (!walletData?.walletHistory) return [];
    
    if (filterType === "credit") {
      return walletData.walletHistory.filter(h => h.type === "credit");
    } else if (filterType === "debit") {
      return walletData.walletHistory.filter(h => h.type === "debit");
    }
    return walletData.walletHistory;
  };

  const getTypeIcon = (type) => {
    return type === "credit" ? 
      <FaPlusCircle className="text-green-500" size={12} /> : 
      <FaMinusCircle className="text-red-500" size={12} />;
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffTime = Math.abs(now - date);
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    
    if (diffDays === 0) {
      return "Today";
    } else if (diffDays === 1) {
      return "Yesterday";
    } else if (diffDays < 7) {
      return `${diffDays} days ago`;
    } else {
      return date.toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" });
    }
  };

  const getHistorySummary = () => {
    if (!walletData?.walletHistory) return { credits: 0, debits: 0 };
    
    const credits = walletData.walletHistory
      .filter(h => h.type === "credit")
      .reduce((sum, h) => sum + h.coins, 0);
    
    const debits = walletData.walletHistory
      .filter(h => h.type === "debit")
      .reduce((sum, h) => sum + h.coins, 0);
    
    return { credits, debits };
  };

  const summary = getHistorySummary();
  const filteredHistory = getFilteredHistory();
  const displayedHistory = showHistory ? filteredHistory : filteredHistory.slice(0, 5);

  const handleExport = (format) => {
    if (!filteredHistory.length) {
      toast.warning("No transactions to export");
      return;
    }

    const exportData = filteredHistory.map(t => ({
      "Date": new Date(t.createdAt).toLocaleString(),
      "Type": t.type.toUpperCase(),
      "Coins": t.type === "credit" ? `+${t.coins}` : `-${t.coins}`,
      "Message": t.message,
      "Transaction ID": t._id
    }));

    if (format === "excel") {
      const ws = XLSX.utils.json_to_sheet(exportData);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, "Wallet History");
      XLSX.writeFile(wb, `wallet_history_${new Date().toISOString().split('T')[0]}.xlsx`);
      toast.success("Exported to Excel successfully!");
    } else if (format === "csv") {
      const ws = XLSX.utils.json_to_sheet(exportData);
      const csv = XLSX.utils.sheet_to_csv(ws);
      const blob = new Blob([csv], { type: "text/csv" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `wallet_history_${new Date().toISOString().split('T')[0]}.csv`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success("Exported to CSV successfully!");
    }
    setShowExportMenu(false);
  };

  if (loading) {
    return (
      <div className="w-full h-full flex items-center justify-center min-h-[400px]">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-indigo-200 border-t-indigo-600 rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-sm text-gray-500 font-medium">Loading wallet data...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="w-full h-full flex items-center justify-center min-h-[400px]">
        <div className="text-center max-w-md px-6">
          <div className="w-20 h-20 bg-red-50 rounded-full flex items-center justify-center mx-auto mb-5">
            <FaWallet className="text-red-400 text-3xl" />
          </div>
          <h3 className="text-lg font-bold text-gray-800 mb-2">Unable to Load Wallet</h3>
          <p className="text-sm text-gray-500 mb-6">{error}</p>
          <button
            onClick={fetchWalletData}
            className="px-6 py-2.5 bg-gradient-to-r from-green-500 to-blue-600 text-white rounded-xl text-xs font-bold hover:opacity-90 transition shadow-md"
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="w-full h-full">
      {/* Header with Wallet Icon - No Back Arrow */}
      <div className="flex items-center justify-between gap-4 mb-6">
        <div className="flex items-center gap-4">
          <div className="p-3 bg-gradient-to-br from-green-50 to-blue-50 rounded-xl text-blue-600 shadow-sm border border-blue-100/50">
            <div className="w-10 h-10 rounded-full bg-gradient-to-r from-green-500 to-blue-600 flex items-center justify-center text-white shadow-md">
              <FaWallet size={20} />
            </div>
          </div>
          <div>
            <h1 className="text-2xl font-bold bg-clip-text text-transparent bg-gradient-to-r from-green-500 to-blue-600">
              My Wallet
            </h1>
            <p className="text-xs text-gray-500 mt-0.5">Track your earnings and transactions</p>
          </div>
        </div>
        
        {/* Export Button */}
        <div className="relative">
          <button
            onClick={() => setShowExportMenu(!showExportMenu)}
            className="flex items-center gap-2 px-4 py-2 bg-indigo-50 text-indigo-600 rounded-xl text-[10px] font-bold uppercase tracking-wider hover:bg-indigo-100 transition-all border border-indigo-200"
          >
            <FaDownload size={12} /> Export
          </button>
          
          <AnimatePresence>
            {showExportMenu && (
              <motion.div
                initial={{ opacity: 0, y: -10, scale: 0.95 }}
                animate={{ opacity: 1, y: 0, scale: 1 }}
                exit={{ opacity: 0, y: -10, scale: 0.95 }}
                className="absolute right-0 mt-2 w-40 bg-white rounded-xl shadow-lg border border-gray-100 z-50 overflow-hidden"
              >
                <button
                  onClick={() => handleExport("excel")}
                  className="w-full px-4 py-3 text-left text-[11px] font-bold text-gray-700 hover:bg-indigo-50 transition-colors flex items-center gap-2"
                >
                  📊 Excel (.xlsx)
                </button>
                <button
                  onClick={() => handleExport("csv")}
                  className="w-full px-4 py-3 text-left text-[11px] font-bold text-gray-700 hover:bg-indigo-50 transition-colors flex items-center gap-2 border-t border-gray-50"
                >
                  📄 CSV (.csv)
                </button>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </div>

      {/* Wallet Balance Card - Same Blue/Green Gradient as Dashboard */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        whileHover={{ y: -2 }}
        className="bg-gradient-to-r from-green-500 to-blue-600 rounded-2xl p-6 mb-8 shadow-xl shadow-blue-100"
      >
        <div className="flex items-start justify-between">
          <div>
            <p className="text-white/80 text-xs font-medium uppercase tracking-wider mb-2 flex items-center gap-2">
              <FaWallet size={12} />
              Total Balance
            </p>
            <div className="flex items-baseline gap-2">
              <motion.span 
                key={walletData?.totalCoins}
                initial={{ scale: 0.5, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                className="text-5xl font-black text-white"
              >
                {walletData?.totalCoins || 0}
              </motion.span>
              <span className="text-white/90 text-sm font-bold flex items-center gap-1">
                <FaCoins size={14} /> Coins
              </span>
            </div>
            <p className="text-white/70 text-[10px] mt-2 font-medium">
              {walletData?.name || walletData?.email?.split('@')[0] || "Candidate"}
            </p>
          </div>
          <motion.div 
            animate={{ rotate: [0, 10, -10, 0] }}
            transition={{ duration: 0.5, delay: 0.3 }}
            className="bg-white/20 backdrop-blur-sm rounded-2xl p-3"
          >
            <FaCoins className="text-white text-3xl" />
          </motion.div>
        </div>
      </motion.div>

      {/* Quick Stats Row */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-8">
        {[
          { label: "Total Earned", value: summary.credits, icon: FaPlusCircle, color: "green", bgColor: "bg-green-50", textColor: "text-green-600" },
          { label: "Total Spent", value: summary.debits, icon: FaMinusCircle, color: "red", bgColor: "bg-red-50", textColor: "text-red-600" },
          { label: "Transactions", value: filteredHistory.length, icon: FaExchangeAlt, color: "blue", bgColor: "bg-blue-50", textColor: "text-blue-600" },
        ].map((stat, i) => (
          <motion.div
            key={i}
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: i * 0.1 }}
            whileHover={{ y: -2, scale: 1.02 }}
            className="flex items-center gap-3 px-4 py-3 bg-white border border-gray-100 rounded-xl shadow-sm cursor-pointer"
          >
            <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${stat.bgColor} ${stat.textColor}`}>
              <stat.icon className="text-sm" />
            </div>
            <div>
              <p className="text-[10px] font-bold text-gray-400 uppercase tracking-wider">{stat.label}</p>
              <motion.p 
                key={stat.value}
                initial={{ scale: 1.2 }}
                animate={{ scale: 1 }}
                className="text-lg font-bold text-gray-800"
              >
                {stat.value}
                {stat.label !== "Transactions" && <span className="text-xs text-gray-400 ml-1">coins</span>}
              </motion.p>
            </div>
          </motion.div>
        ))}
      </div>

      {/* History Section Header with Filter */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <div className="w-1 h-5 bg-gradient-to-b from-green-500 to-blue-600 rounded-full"></div>
          <h3 className="text-xs font-bold text-gray-700 uppercase tracking-wider flex items-center gap-2">
            <FaHistory size={12} />
            Transaction History
          </h3>
        </div>
        <div className="flex items-center gap-3">
          {/* Filter Dropdown */}
          <div className="relative">
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={() => setShowFilterDropdown(!showFilterDropdown)}
              className={`flex items-center gap-2 px-3 py-1.5 rounded-lg text-[10px] font-bold uppercase tracking-wider transition-all ${
                filterType !== "all" 
                  ? "bg-gradient-to-r from-green-500 to-blue-600 text-white" 
                  : "bg-gray-100 text-gray-600 hover:bg-gray-200"
              }`}
            >
              <FaFilter size={10} />
              {filterType === "all" ? "All" : filterType === "credit" ? "Credits" : "Debits"}
            </motion.button>
            
            <AnimatePresence>
              {showFilterDropdown && (
                <motion.div
                  initial={{ opacity: 0, y: -10, scale: 0.95 }}
                  animate={{ opacity: 1, y: 0, scale: 1 }}
                  exit={{ opacity: 0, y: -10, scale: 0.95 }}
                  className="absolute right-0 mt-2 w-36 bg-white rounded-xl shadow-lg border border-gray-100 z-50 overflow-hidden"
                >
                  <button
                    onClick={() => { setFilterType("all"); setShowFilterDropdown(false); }}
                    className="w-full px-4 py-2.5 text-left text-[11px] font-bold text-gray-700 hover:bg-green-50 transition-colors flex items-center gap-2"
                  >
                    📋 All Transactions
                  </button>
                  <button
                    onClick={() => { setFilterType("credit"); setShowFilterDropdown(false); }}
                    className="w-full px-4 py-2.5 text-left text-[11px] font-bold text-green-600 hover:bg-green-50 transition-colors flex items-center gap-2 border-t border-gray-50"
                  >
                    💚 Credits Only
                  </button>
                  <button
                    onClick={() => { setFilterType("debit"); setShowFilterDropdown(false); }}
                    className="w-full px-4 py-2.5 text-left text-[11px] font-bold text-red-600 hover:bg-green-50 transition-colors flex items-center gap-2 border-t border-gray-50"
                  >
                    ❤️ Debits Only
                  </button>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
          
          {filterType !== "all" && (
            <motion.button
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: 1, scale: 1 }}
              onClick={() => setFilterType("all")}
              className="flex items-center gap-1 px-2 py-1 rounded-lg text-[9px] font-bold text-gray-400 hover:text-gray-600"
            >
              <FaTimes size={10} /> Clear
            </motion.button>
          )}
          
          {filteredHistory.length > 5 && (
            <button
              onClick={() => setShowHistory(!showHistory)}
              className="text-[10px] font-bold text-blue-600 hover:text-blue-800 transition-colors"
            >
              {showHistory ? "Show Less" : "View All"}
            </button>
          )}
        </div>
      </div>

      {/* Transaction History Table */}
      <motion.div 
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.2 }}
        className="bg-white rounded-xl border border-gray-100 shadow-sm overflow-hidden mb-8"
      >
        {filteredHistory.length > 0 ? (
          <div className="overflow-x-auto">
            <table className="min-w-full">
              <thead className="text-xs text-left text-white bg-gradient-to-r from-green-500 to-blue-600">
                <tr>
                  <th className="py-3 px-4 text-center font-bold">Type</th>
                  <th className="py-3 px-4 text-center font-bold">Coins</th>
                  <th className="py-3 px-4 text-center font-bold">Message</th>
                  <th className="py-3 px-4 text-center font-bold">Date</th>
                </tr>
              </thead>
              <tbody>
                <AnimatePresence>
                  {displayedHistory.map((transaction, idx) => (
                    <motion.tr
                      key={transaction._id || idx}
                      initial={{ opacity: 0, x: -20 }}
                      animate={{ opacity: 1, x: 0 }}
                      exit={{ opacity: 0, x: 20 }}
                      transition={{ delay: idx * 0.05 }}
                      whileHover={{ backgroundColor: "#f0fdf4" }}
                      className="border-b border-gray-50 hover:bg-green-50/30 transition-colors cursor-pointer"
                    >
                      <td className="px-4 py-3 text-center">
                        <motion.span 
                          whileHover={{ scale: 1.05 }}
                          className={`inline-flex items-center gap-1.5 text-[10px] font-bold px-2 py-1 rounded-full ${
                            transaction.type === "credit" 
                              ? "bg-green-100 text-green-700" 
                              : "bg-red-100 text-red-700"
                          }`}
                        >
                          {getTypeIcon(transaction.type)}
                          {transaction.type === "credit" ? "Credit" : "Debit"}
                        </motion.span>
                      </td>
                      <td className="px-4 py-3 text-center">
                        <motion.span 
                          initial={{ scale: 1 }}
                          animate={{ scale: [1, 1.2, 1] }}
                          transition={{ delay: idx * 0.05, duration: 0.3 }}
                          className={`text-xs font-bold ${
                            transaction.type === "credit" ? "text-green-600" : "text-red-600"
                          }`}
                        >
                          {transaction.type === "credit" ? "+" : "-"}{transaction.coins}
                        </motion.span>
                      </td>
                      <td className="px-4 py-3 text-center">
                        <p className="text-xs text-gray-700 font-medium">{transaction.message}</p>
                      </td>
                      <td className="px-4 py-3 text-center">
                        <div className="flex items-center justify-center gap-1.5">
                          <FaClock className="text-gray-300 text-[9px]" />
                          <span className="text-[10px] text-gray-500 font-medium">
                            {formatDate(transaction.createdAt)}
                          </span>
                        </div>
                      </td>
                    </motion.tr>
                  ))}
                </AnimatePresence>
              </tbody>
            </table>
          </div>
        ) : (
          <motion.div 
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="py-12 text-center"
          >
            <div className="w-16 h-16 bg-gray-50 rounded-full flex items-center justify-center mx-auto mb-4">
              <FaHistory className="text-gray-300 text-2xl" />
            </div>
            <h3 className="text-sm font-bold text-gray-700 mb-1">No Transactions Found</h3>
            <p className="text-[11px] text-gray-400">
              {filterType !== "all" ? `No ${filterType} transactions available` : "Your transaction history will appear here"}
            </p>
          </motion.div>
        )}
      </motion.div>

      {/* How to Earn Coins Section */}
      <motion.div 
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.3 }}
        whileHover={{ y: -3 }}
        className="bg-gradient-to-r from-green-50 to-blue-50 rounded-xl p-5 border border-green-100 mb-8"
      >
        <div className="flex items-center gap-2 mb-3">
          <motion.div
            animate={{ rotate: [0, 10, -10, 0] }}
            transition={{ duration: 1, repeat: Infinity, repeatDelay: 3 }}
          >
            <FaGift className="text-green-500 text-sm" />
          </motion.div>
          <h4 className="text-xs font-bold text-gray-800 uppercase tracking-wider">How to Earn Coins</h4>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          {[
            { action: "Apply for a Job", reward: "20 Coins", icon: FaCheckCircle, color: "green" },
            { action: "Complete Assessment", reward: "10 Coins", icon: FaCheckCircle, color: "blue" },
            { action: "Attend Interview", reward: "50 Coins", icon: FaCheckCircle, color: "indigo" },
          ].map((item, i) => (
            <motion.div 
              key={i}
              whileHover={{ scale: 1.02, x: 5 }}
              className="flex items-center gap-2 bg-white/60 rounded-lg px-3 py-2 cursor-pointer"
            >
              <item.icon className={`text-${item.color}-500 text-[10px]`} />
              <span className="text-[10px] font-medium text-gray-700 flex-1">{item.action}</span>
              <motion.span 
                whileHover={{ scale: 1.1 }}
                className="text-[10px] font-bold text-green-600"
              >
                {item.reward}
              </motion.span>
            </motion.div>
          ))}
        </div>
      </motion.div>

      {/* Rewards & Benefits Section */}
      <motion.div 
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.4 }}
        className="bg-white rounded-xl border border-gray-100 p-5 shadow-sm"
      >
        <div className="flex items-center gap-2 mb-4">
          <FaTrophy className="text-green-500 text-sm" />
          <h4 className="text-xs font-bold text-gray-800 uppercase tracking-wider">Rewards & Benefits</h4>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <motion.div 
            whileHover={{ scale: 1.02, x: 5 }}
            className="flex items-start gap-3 p-3 bg-gradient-to-r from-green-50 to-emerald-50 rounded-xl cursor-pointer"
          >
            <div className="w-8 h-8 rounded-full bg-green-100 flex items-center justify-center text-green-600">
              <FaChartLine size={14} />
            </div>
            <div>
              <p className="text-[11px] font-bold text-gray-800">Top Performer Bonus</p>
              <p className="text-[9px] text-gray-500 mt-0.5">Earn extra 100 coins for being in top 10%</p>
            </div>
          </motion.div>
          <motion.div 
            whileHover={{ scale: 1.02, x: 5 }}
            className="flex items-start gap-3 p-3 bg-gradient-to-r from-blue-50 to-indigo-50 rounded-xl cursor-pointer"
          >
            <div className="w-8 h-8 rounded-full bg-blue-100 flex items-center justify-center text-blue-600">
              <FaUserFriends size={14} />
            </div>
            <div>
              <p className="text-[11px] font-bold text-gray-800">Referral Program</p>
              <p className="text-[9px] text-gray-500 mt-0.5">Refer a friend and earn 200 coins</p>
            </div>
          </motion.div>
        </div>
      </motion.div>

      {/* Footer */}
      <motion.div 
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.5 }}
        className="flex items-center justify-center gap-2 text-[9px] font-bold text-gray-400 uppercase tracking-[0.2em] py-6 mt-4"
      >
        <FaInfoCircle className="text-blue-400" />
        Timely Health Recruitment Portal • {new Date().getFullYear()}
      </motion.div>
    </div>
  );
};

export default CandidateWallet;